import { Avatar, Badge, Group, Indicator, Paper, Stack, Text } from '@mantine/core';
import { IconCrown, IconPointFilled } from '@tabler/icons-react';
import { HOST_SEAT_ROLE_LABEL } from '@creativitas/shared';
import type { ReactNode } from 'react';

export function initialsOf(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');
}

type UserChipProps = {
  name: string;
  secondary?: string;
  color?: string;
  size?: 'sm' | 'md';
};

/** Compact identity chip used in headers and lists. */
export function UserChip({ name, secondary, color, size = 'md' }: UserChipProps) {
  return (
    <Group gap="sm" wrap="nowrap">
      <Avatar
        radius="xl"
        size={size === 'sm' ? 28 : 38}
        color="teal"
        style={color ? { background: color } : undefined}
        /*
          Mantine draws the initials in an inner element with its own colour, so
          `color` on the root never reaches them. Without this the letters stay
          brand teal — on a saturated pawn colour that is barely 1.3:1.
        */
        styles={color ? { placeholder: { color: 'white' } } : undefined}
      >
        {initialsOf(name)}
      </Avatar>
      <Stack gap={0} style={{ minWidth: 0 }}>
        <Text size={size === 'sm' ? 'sm' : 'md'} fw={600} truncate>
          {name}
        </Text>
        {secondary ? (
          <Text size="xs" c="var(--text-muted)" truncate>
            {secondary}
          </Text>
        ) : null}
      </Stack>
    </Group>
  );
}

type ParticipantItemProps = {
  name: string;
  /** Turn order comes from join order (§31). */
  joinOrder: number;
  laneColor?: string | null;
  /**
   * Ведущий, занявший игровое место. В этом списке только игроки, поэтому
   * роль у него двойная: он и ведёт партию, и ходит наравне с остальными.
   */
  isHostSeat?: boolean;
  /** Marks the row a guest should read as themselves once the table is open. */
  isYou?: boolean;
  isCurrentTurn?: boolean;
  isConnected?: boolean;
  isRemoved?: boolean;
  /** Position badge, facilitator menu — whatever sits on the identity row. */
  trailing?: ReactNode;
  /**
   * A second row inside the same card: resource counters and money. Keeping it
   * here rather than in a card of its own is what stops the roster and the
   * player state from listing every name twice.
   */
  footer?: ReactNode;
};

/** A participant as shown in the session roster and on the game screen. */
export function ParticipantItem({
  name,
  joinOrder,
  laneColor,
  isHostSeat,
  isYou,
  isCurrentTurn,
  isConnected = true,
  isRemoved,
  trailing,
  footer,
}: ParticipantItemProps) {
  return (
    <Paper
      withBorder
      p="sm"
      radius="md"
      style={{
        borderColor: isCurrentTurn ? 'var(--accent)' : 'var(--border-subtle)',
        background: isCurrentTurn ? 'var(--surface-accent-soft)' : 'var(--surface-card)',
        opacity: isRemoved ? 0.5 : 1,
      }}
    >
      <Stack gap="xs">
        <Group justify="space-between" wrap="nowrap" gap="sm">
          <Group gap="sm" wrap="nowrap" style={{ minWidth: 0 }}>
            <Indicator
              size={9}
              offset={4}
              withBorder
              disabled={isRemoved}
              color={isConnected ? 'green' : 'gray'}
              label={undefined}
            >
              <Avatar
                radius="xl"
                size={36}
                style={laneColor ? { background: laneColor } : undefined}
                /* The initials live in an inner element — see UserChip above. */
                styles={laneColor ? { placeholder: { color: 'white' } } : undefined}
                color="teal"
              >
                {initialsOf(name)}
              </Avatar>
            </Indicator>

            <Stack gap={2} style={{ minWidth: 0 }}>
              <Group gap={6} wrap="nowrap">
                <Text fw={600} size="sm" truncate>
                  {name}
                </Text>
                {isHostSeat ? (
                  <Badge
                    size="xs"
                    variant="light"
                    color="gold"
                    leftSection={<IconCrown size={11} />}
                  >
                    {HOST_SEAT_ROLE_LABEL}
                  </Badge>
                ) : null}
                {isYou ? (
                  <Badge size="xs" variant="light" color="teal">
                    Вы
                  </Badge>
                ) : null}
                {isRemoved ? (
                  <Badge size="xs" variant="light" color="gray">
                    Удалён
                  </Badge>
                ) : null}
              </Group>

              <Group gap={4} wrap="nowrap">
                <Text size="xs" c="var(--text-muted)">
                  Ход {joinOrder + 1}
                </Text>
                {isCurrentTurn ? (
                  <Badge
                    size="xs"
                    variant="filled"
                    color="teal"
                    leftSection={<IconPointFilled size={10} />}
                  >
                    Сейчас ходит
                  </Badge>
                ) : null}
              </Group>
            </Stack>
          </Group>

          {trailing ? <Group gap="xs" wrap="nowrap">{trailing}</Group> : null}
        </Group>
        {footer}
      </Stack>
    </Paper>
  );
}
